<template>
  <div class="c-details">
    <div class="c-details__main-wrapper">
      <detail-image-cover
        :show-skin-ad="showSkinAd"
        :product="mainProduct"
        :background-only="showPlayer"
      ></detail-image-cover>
      <template v-if="showPlayer">
        <div
          v-show="showSkinAd && skinAd"
          class="c-details__skin-ad"
        >
          <v-ad-banner
            :ad-system="skinAd.adSystem"
            :auto-load="skinAd.autoLoad"
            :slot-params="skinAd.slotParams"
            :space-id="skinAd.id"
            @isEmpty="isSkinAdEmpty = $event"
          ></v-ad-banner>
        </div>
        <div class="o-container u-px-lg-25 u-relative">
          <ui-player
            ref="player"
            :class="{ 'is-ad-visible': showSkinAd }"
          ></ui-player>
        </div>
      </template>
      <div
        v-else-if="isPlayButtonVisible"
        class="c-details__play-button-wrapper"
      >
        <v-play-product
          v-if="isPlayable || isFuture"
          class="c-details__play-button"
          :product="episode || product"
          autoplay-redirect
          show-label
        ></v-play-product>
      </div>
    </div>

    <v-highlight
      :product="product"
      :trailer-mode="TrailerMode.DETAIL"
      :class="{ 'is-below-wrapper': showSkinAd || seasons }"
      class="u-isolate u-bg-white"
    >
      <div
        v-if="parentProduct"
        class="o-column u-flex u-flex-align-center u-mobile-only"
      >
        <router-link
          :to="parentProduct | link"
          class="c-highlight__parent o-typography--action o-typography--underline"
          tag="h2"
        >
          {{ parentProduct.title }}
        </router-link>
      </div>
      <div
        v-if="showPlayer || (!clip && !isProgramme)"
        class="c-highlight__buttons o-column o-column-auto o-order-lg-last"
      >
        <v-share-product
          v-if="mainProduct"
          class="c-details__action-button"
          :product="mainProduct"
        ></v-share-product>
        <v-favourite-toggle
          v-if="!clip && !isProgramme"
          class="c-details__action-button o-button__only-icon"
          :product="product"
        ></v-favourite-toggle>
      </div>
      <div
        class="c-highlight__content o-column"
        :class="[parentProduct ? 'o-column-12 o-column-lg' : 'o-order-first']"
      >
        <router-link
          v-if="parentProduct"
          :to="parentProduct | link"
          class="c-highlight__parent u-desktop-only"
        >
          <h2 class="o-typography--action o-typography--static o-typography--underline">
            {{ parentProduct.title }}
          </h2>
        </router-link>
        <h1 class="c-details__title u-bold">
          {{ title }}
        </h1>
        <div class="c-details__meta u-flex u-flex-align-baseline">
          <v-badge
            v-if="mainProduct"
            :product="mainProduct"
            class="c-highlight__badge"
            counting
          ></v-badge>
          <p class="o-typography__subtitle3 u-bold u-color-primary u-ellipsis-2-lines">
            {{ metaData }}
          </p>
        </div>
      </div>
      <div
        v-show="!highlightAdEmpty"
        class="o-column o-column-12 o-column-lg u-flex u-flex-align-center u-flex-justify-center"
      >
        <div class="c-details__companion-ad">
          <v-ad-banner
            :ad-system="AdSystem.FREEWHEEL"
            :auto-load="false"
            :slot-params="getCompanionSlid"
            space-id="companion_slot"
            @isEmpty="highlightAdEmpty = $event"
          ></v-ad-banner>
        </div>
      </div>
    </v-highlight>

    <div class="c-details__info o-container u-py-25">
      <div class="o-row u-flex-align-start">
        <div class="c-details__info-content o-column">
          <details-info
            v-if="mainProduct"
            :product="mainProduct"
          ></details-info>
          <p
            v-if="availableDays"
            class="c-details__available-until"
          >
            <span class="u-color-accent">{{ $t('product.availableUntil') }} </span>
            <span>{{ $t('product.daysDuration', { count: availableDays}) }}</span>
          </p>
        </div>
        <div class="o-column o-column-12 o-column-lg-4">
          <related-links
            v-if="relatedLinks"
            class="c-details__related-links"
            :related-links="relatedLinks"
          ></related-links>
        </div>
      </div>
    </div>

    <v-ad-banner
      v-if="leaderboard && leaderboard.id"
      class="c-details__leaderboard-ad"
      :ad-system="leaderboard.adSystem"
      :auto-load="leaderboard.autoLoad"
      :slot-params="leaderboard.slotParams"
      :space-id="leaderboard.id"
    ></v-ad-banner>
  </div>
</template>
<script>
  import dayjs from 'dayjs';
  import { VAdBanner, VBadge, VFavouriteToggle, VHighlight, VPlayProduct, VShareProduct } from '@/index/shared/components';
  import { link, meta } from '@/index/shared/filters';
  import { companionBannerSlidMixin, productMixin } from '@/index/shared/mixins';
  import { AdSystem, DisplayScheduleType, ImageType, ProductType, TrailerMode } from '@/shared/enums';
  import UiPlayer from '@/shared/components/player/index.vue';
  import { getActiveScheduleByType } from '@/shared/utils';
  import RelatedLinks from '@/index/components/related-links/index.vue';
  import { VideoType } from '@/shared/libs/player';
  import { getRandomParameters } from '@/shared/plugins/freewheel/helpers';
  import { leaderboardMixin } from '@/shared/mixins';
  import DetailsInfo from './info.vue';
  import DetailImageCover from './image-cover.vue';

  const AD_SKIN_VISIBILITY_BREAK_POINT = 1365;

  export default {
    components: {
      DetailsInfo,
      DetailImageCover,
      RelatedLinks,
      UiPlayer,
      VAdBanner,
      VBadge,
      VHighlight,
      VFavouriteToggle,
      VPlayProduct,
      VShareProduct,
    },
    enums: {
      AdSystem,
      TrailerMode,
      ImageType,
    },
    filters: {
      link,
    },
    mixins: [
      productMixin,
      companionBannerSlidMixin,
      leaderboardMixin,
    ],
    props: {
      product: {
        type: Object,
        required: true,
      },
      seasons: Object,
      episode: Object,
      clip: Object,
      channel: Object,
    },
    data() {
      return {
        info: this.$services.resolve('info'),
        time: this.$services.resolve('time'),
        isSkinAdEmpty: true,
        highlightAdEmpty: true,
      };
    },
    computed: {
      showPlayer() {
        return !this.seasons;
      },
      relatedLinks() {
        return this.product?.links?.length && this.product.links;
      },
      title() {
        return this.clip?.title || this.mainProduct.title;
      },
      mainProduct() {
        if (this.seasons) {
          return this.product;
        }

        if (this.clip) {
          return this.clip;
        }

        return this.episode || this.product;
      },
      parentProduct() {
        if (this.product.parentProduct) {
          if (this.product.parentProduct.type === ProductType.SEASON) {
            return this.product.parentProduct.serial;
          }

          return this.product.parentProduct;
        }

        if ([ProductType.CLIP, ProductType.EPISODE].includes(this.mainProduct.type)) {
          return this.product;
        }
      },
      isPlayButtonVisible() {
        if (this.product?.type === ProductType.SERIAL) {
          return this.episode;
        }

        return this.product;
      },
      showSkinAd() {
        return !this.seasons && !this.isSkinAdEmpty && this.$window.innerWidth > AD_SKIN_VISIBILITY_BREAK_POINT;
      },
      leaderboard() {
        if (!this.mainProduct) {
          return {};
        }

        return this.getLeaderboardData(this.info.platformSettings);
      },
      skinAd() {
        const adSystem = this.info.adSystemForSkinAd || AdSystem.FREEWHEEL;

        if (adSystem === AdSystem.FREEWHEEL) {
          const id = 'ad_banner_skin';
          const { vprn, pvrn } = getRandomParameters();
          return {
            adSystem,
            autoLoad: false,
            slotParams: `slid=${id}&&vprn=${vprn}&pvrn=${pvrn}&ptgt=s&w=1920&h=720&slau=1920x720 Skin&tpcl=DISPLAY&flag=+cmpn+fcai`,
            id,
          };
        }

        if (adSystem === AdSystem.SMART_AD) {
          return {
            adSystem,
            autoLoad: true,
            slotParams: '',
            id: this.info.platformSettings.smartAdContainerForSkinAd,
          };
        }

        return {};
      },
      isMobile() {
        return this.$window.isMobileView;
      },
      availableDays() {
        const till = this.isProgramme
          ? this.mainProduct.catchupTill
          : getActiveScheduleByType(this.mainProduct, DisplayScheduleType.NORMAL)?.till;

        if (!till) {
          return 0;
        }
        const days = dayjs(till).add(1, 'day').diff(this.time.now, 'days');

        if (days > 0 && days <= 30) {
          return days;
        }
      },
      isProgramme() {
        return this.product?.type === ProductType.PROGRAMME;
      },
      isClip() {
        return this.product?.type === ProductType.CLIP || this.clip;
      },
      isSerialOrEpisode() {
        return [ProductType.EPISODE, ProductType.SERIAL].includes(this.product.type);
      },
      metaData() {
        if (this.clip) {
          return meta(this.clip, ['sinceDateFormat', 'rating', 'duration']);
        }

        if (this.isProgramme) {
          return meta(this.product, 'availableFrom');
        }

        if (this.seasons) {
          return meta({ ...this.product, seasons: this.seasons.collection.records }, ['year', 'rating', 'numberOfSeasons']);
        }

        if (this.episode) {
          return meta(this.episode, ['seasonFormat', 'year', 'rating']);
        }

        return meta(this.product, ['year', 'rating', 'duration', 'originalTitle']);
      },
    },
    mounted() {
      this.$refs.player?.load(this.getPlayerProduct());
    },
    methods: {
      getPlayerProduct() {
        if (this.isClip) {
          return { product: this.clip, videoType: VideoType.MOVIE };
        }

        if (this.isSerialOrEpisode) {
          return { product: this.episode || this.product, videoType: VideoType.MOVIE };
        }

        if (this.isProgramme) {
          const isCatchup = dayjs(this.time.now).isAfter(dayjs(this.product.till));

          if (isCatchup) {
            const catchupUnavailable = !this.info.freeCatchupAvailable || !this.product.catchupTill || dayjs(this.time.now).isAfter(dayjs(this.product.catchupTill));

            return { product: this.product, videoType: VideoType.CATCHUP, catchupUnavailable };
          }

          return { product: { ...this.channel, currentProgram: this.product }, videoType: VideoType.LIVE };
        }

        return { product: this.product, videoType: VideoType.MOVIE };
      },
    },
  };
</script>
